// app/src/features/timeline/useTimelineMouse.js
// Moved verbatim out of AppInner (app/src/App.jsx) by PKG-04 module #1; reads props and selectors, never the store.
import { useCallback } from "react";
import { clientToLocalX } from "../../lib/timeline/geometry.js";

/** The timeline's mouse handlers: press (pan anchor / shift-select), move, release and leave. */
export function useTimelineMouse({ geom, setTsdH, setTsdIsDragging, tsdDragRef }) {
  const {
    containerRef,
    dragRef,
    evtScrollRef,
    ppdRef,
    scheduleRender,
    screenToDay,
    selRect,
    selectRef,
    setCrossX,
    setSelRect,
    vlRef,
  } = geom;

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- the refs and setters arrive through the hook's argument object (stable: useRef/useState in useTimelineGeometry and useTsdDrawer); the manual deps are verbatim from AppInner
  const onMD = useCallback((e) => {
    if (e.button !== 0) return;
    // A press on a CONTROL inside the timeline (a lane panel's button, a link, a field) is
    // that control's — it must not start a pan (and its preventDefault), or a small move
    // between press and release drags the timeline under the pointer and the click lands
    // somewhere else.
    if (e.target?.closest?.("button, a, input, textarea, select, [role='button']")) return;
    if (e.shiftKey) {
      selectRef.current = { screenX: e.clientX, screenY: e.clientY };
      setSelRect({ left: e.clientX, top: e.clientY, width: 0, height: 0 });
      e.preventDefault();
      return;
    }
    // Store the drag anchor in timeline-LOCAL px (zoom-inverted), so the pan
    // delta below divides by ppd (also local px/day) consistently — otherwise
    // the Dark theme's CSS zoom makes panning move at the wrong speed.
    const cont = containerRef.current;
    const scrollEl = evtScrollRef.current;
    dragRef.current = {
      startX: cont ? clientToLocalX(cont, e.clientX).x : e.clientX,
      startVL: vlRef.current,
      startY: e.clientY,
      startScrollTop: scrollEl ? scrollEl.scrollTop : 0,
    };
    e.preventDefault();
  }, []);

  const onMM = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- the refs and setters arrive through the hook's argument object (stable: useRef/useState in useTimelineGeometry and useTsdDrawer); the manual deps are verbatim from AppInner
    (e) => {
      if (tsdDragRef.current) {
        const delta = tsdDragRef.current.startY - e.clientY;
        let newH = Math.max(0, Math.min(400, tsdDragRef.current.startH + delta));
        if (newH < 400 * 0.05) newH = 0;
        else if (newH > 400 * 0.95) newH = 400;
        setTsdH(newH);
        return;
      }
      const cont = containerRef.current;
      if (cont) {
        const { x: cx, width: cw } = clientToLocalX(cont, e.clientX);
        setCrossX(cx >= 0 && cx <= cw ? cx : null);
      }
      if (selectRef.current) {
        const s = selectRef.current;
        setSelRect({
          left: Math.min(s.screenX, e.clientX),
          top: Math.min(s.screenY, e.clientY),
          width: Math.abs(e.clientX - s.screenX),
          height: Math.abs(e.clientY - s.screenY),
        });
        return;
      }
      if (dragRef.current) {
        if (!e.buttons) {
          dragRef.current = null;
          return;
        }
        const lx = cont ? clientToLocalX(cont, e.clientX).x : e.clientX;
        vlRef.current = dragRef.current.startVL + (dragRef.current.startX - lx) / ppdRef.current;
        // Vertical grab: dragging DOWN reveals content above (scrollTop decreases),
        // mirroring the horizontal grab feel — same evt-scroll container the two-finger
        // scroll + arrow keys use. Only when the branch stack actually overflows.
        const scrollEl = evtScrollRef.current;
        if (scrollEl) {
          scrollEl.scrollTop =
            dragRef.current.startScrollTop + (dragRef.current.startY - e.clientY);
        }
        scheduleRender();
      }
    },
    [scheduleRender], // eslint-disable-line react-hooks/exhaustive-deps -- setTsdH/tsdDragRef are stable (useState setter + useRef) through the useTsdDrawer hook boundary
  );

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- the refs and setters arrive through the hook's argument object (stable: useRef/useState in useTimelineGeometry and useTsdDrawer); the manual deps are verbatim from AppInner
  const onMU = useCallback(() => {
    tsdDragRef.current = null;
    setTsdIsDragging(false);
    if (selectRef.current && selRect && selRect.width > 20) {
      const cont = containerRef.current;
      if (cont) {
        const leftDay = screenToDay(clientToLocalX(cont, selRect.left).x);
        const rightDay = screenToDay(clientToLocalX(cont, selRect.left + selRect.width).x);
        const span = rightDay - leftDay;
        if (span > 0) {
          const vw2 = cont.offsetWidth;
          const newPpd = Math.max(0.08, Math.min(1000, vw2 / span));
          ppdRef.current = newPpd;
          vlRef.current = leftDay;
          scheduleRender();
        }
      }
    }
    selectRef.current = null;
    setSelRect(null);
    dragRef.current = null;
  }, [selRect, screenToDay, scheduleRender]); // eslint-disable-line react-hooks/exhaustive-deps -- setTsdIsDragging/tsdDragRef are stable (useState setter + useRef) through the useTsdDrawer hook boundary

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- the refs and setters arrive through the hook's argument object (stable: useRef/useState in useTimelineGeometry and useTsdDrawer); the manual deps are verbatim from AppInner
  const onML = useCallback(() => {
    tsdDragRef.current = null;
    setTsdIsDragging(false);
    selectRef.current = null;
    setSelRect(null);
    dragRef.current = null;
    setCrossX(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- setTsdIsDragging/tsdDragRef are stable (useState setter + useRef) through the useTsdDrawer hook boundary
  return { onMD, onMM, onMU, onML };
}
